import { formatDate as formatDateIntl, Logo, Printer, X } from "@ews-admin/global-design-system";
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { FacilityInvoice } from "../../../types/payment";
import { formatMoney, toIntlLocale } from "../../../utils/format.utils";
import { formatBillingPeriod, formatDateOnly, invoiceDisplayStatus } from "../../../utils/invoice.utils";
import { InvoiceStatusBadge } from "./InvoiceStatusBadge";

// While the detail is open, `@media print` in index.css hides every other child of
// <body> (the app root, toasts, overlays) so the browser prints only the invoice.
export const INVOICE_PRINT_BODY_CLASS = "invoice-print-mode";

const CONTRACT_DURATION_KEYS: Record<string, string> = {
  ONE_MONTH: "licence.duration.oneMonth",
  ONE_YEAR: "licence.duration.oneYear",
  THREE_YEARS: "licence.duration.threeYears",
  FIVE_YEARS: "licence.duration.fiveYears",
};

const BILLING_MODE_KEYS: Record<string, string> = {
  ANNUAL_LUMP_SUM: "licence.invoices.detail.billingModes.annualLumpSum",
  MONTHLY: "licence.invoices.detail.billingModes.monthly",
};

interface InvoiceDetailProps {
  invoice: FacilityInvoice;
  facilityName?: string | null;
  onClose: () => void;
}

export const InvoiceDetail: React.FC<InvoiceDetailProps> = ({ invoice, facilityName, onClose }) => {
  const { t, i18n } = useTranslation();
  const fmt = (amount: number | null | undefined) => formatMoney(amount, i18n.language);
  const status = invoiceDisplayStatus(invoice.status, invoice.dueDate);

  useEffect(() => {
    document.body.classList.add(INVOICE_PRINT_BODY_CLASS);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.classList.remove(INVOICE_PRINT_BODY_CLASS);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const licenceTypeLabel = invoice.licenceType
    ? t(`licence.plans.${invoice.licenceType.toLowerCase().replace("professionnel", "professional")}`, invoice.licenceType)
    : "—";
  const contractDurationLabel = invoice.contractDuration
    ? t(CONTRACT_DURATION_KEYS[invoice.contractDuration] ?? invoice.contractDuration, invoice.contractDuration)
    : "—";
  // Most licences have no billing mode stored (only a one-year contract can choose one):
  // the invoice's own period then says what it covered.
  const billingModeLabel = invoice.paymentBillingMode
    ? t(BILLING_MODE_KEYS[invoice.paymentBillingMode] ?? invoice.paymentBillingMode, invoice.paymentBillingMode)
    : invoice.periodMonths && invoice.periodMonths > 1
      ? t("licence.invoices.detail.billingModes.grouped", { count: invoice.periodMonths })
      : invoice.periodMonths === 1
        ? t("licence.invoices.detail.billingModes.monthly")
        : "—";
  const hasPromotion = invoice.baseAmount != null && invoice.rebatePercentage != null;
  // payment_date is an instant: format it in the viewer's timezone, unlike the date-only fields.
  const paymentDate = invoice.paymentDate
    ? formatDateIntl(invoice.paymentDate, { month: "short", locale: toIntlLocale(i18n.language) })
    : formatDateOnly(invoice.paidDate, i18n.language);

  return createPortal(
    <div
      className="invoice-print-root fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50 sm:items-center"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-detail-title"
        onClick={(e) => e.stopPropagation()}
        className="invoice-print-sheet w-full max-w-2xl bg-white shadow-xl rounded-2xl"
      >
        <div className="p-6 space-y-6 sm:p-8">
          {/* Issuer / recipient header */}
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <Logo size="md" />
              <p className="mt-2 text-sm font-semibold text-neutral-900">Medecine360</p>
            </div>
            <div className="text-right">
              <h2 id="invoice-detail-title" className="text-xl font-bold text-neutral-900">
                {t("licence.invoices.detail.title", { number: invoice.id })}
              </h2>
              {invoice.createdAt && (
                <p className="mt-1 text-xs text-neutral-500">
                  {t("licence.invoices.detail.issuedOn")}:{" "}
                  {formatDateIntl(invoice.createdAt, { month: "short", locale: toIntlLocale(i18n.language) })}
                </p>
              )}
              <div className="mt-2">
                <InvoiceStatusBadge status={status} rawStatus={invoice.status} />
              </div>
            </div>
          </div>

          <div className="p-4 rounded-xl bg-neutral-50">
            <p className="text-xs font-medium tracking-wide uppercase text-neutral-500">
              {t("licence.invoices.detail.billedTo")}
            </p>
            <p className="mt-1 font-semibold text-neutral-900" data-testid="invoice-detail-facility">
              {facilityName || "—"}
            </p>
          </div>

          <dl className="grid grid-cols-1 text-sm gap-x-6 gap-y-3 sm:grid-cols-2">
            <DetailRow label={t("licence.invoices.detail.licenceType")} value={licenceTypeLabel} />
            <DetailRow
              label={t("licence.invoices.detail.numberOfDoctors")}
              value={invoice.numberOfDoctors != null ? String(invoice.numberOfDoctors) : "—"}
            />
            <DetailRow label={t("licence.invoices.detail.contractDuration")} value={contractDurationLabel} />
            <DetailRow label={t("licence.invoices.detail.billingMode")} value={billingModeLabel} />
            <DetailRow
              label={t("licence.invoices.detail.period")}
              value={formatBillingPeriod(invoice.billingPeriod, i18n.language, invoice.periodMonths)}
            />
            <DetailRow label={t("licence.invoices.detail.dueDate")} value={formatDateOnly(invoice.dueDate, i18n.language)} />
          </dl>

          <div className="pt-4 space-y-2 text-sm border-t border-neutral-200">
            {hasPromotion && (
              <>
                <AmountRow label={t("licence.invoices.detail.baseAmount")} value={fmt(invoice.baseAmount)} />
                <AmountRow
                  label={t("payment.invoice.promotionLine", {
                    code: invoice.promotionCode ?? "",
                    rebate: invoice.rebatePercentage,
                  })}
                  value={`-${fmt((invoice.baseAmount ?? 0) - invoice.amount)}`}
                  className="text-success-600"
                />
              </>
            )}
            <div className="flex justify-between pt-2 text-base font-bold border-t border-neutral-200 text-neutral-900">
              <span>{t("licence.invoices.detail.total")}</span>
              <span data-testid="invoice-detail-total">{fmt(invoice.amount)}</span>
            </div>
          </div>

          {status === "paid" && (
            <div className="p-4 space-y-2 text-sm border rounded-xl border-success-200 bg-success-50">
              <p className="font-semibold text-success-800">{t("licence.invoices.detail.payment")}</p>
              <AmountRow label={t("licence.invoices.detail.paymentDate")} value={paymentDate} />
              <AmountRow label={t("licence.invoices.detail.paymentMethod")} value={invoice.paymentMethod || "—"} />
              <AmountRow
                label={t("licence.invoices.detail.paymentReference")}
                value={invoice.paymentReference || "—"}
              />
            </div>
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-3 px-6 py-4 border-t invoice-print-hide border-neutral-100 sm:px-8">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border rounded-lg border-neutral-200 text-neutral-700 hover:bg-neutral-50"
          >
            <X className="w-4 h-4" />
            {t("licence.invoices.detail.close")}
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg bg-primary-600 hover:bg-primary-700"
          >
            <Printer className="w-4 h-4" />
            {t("licence.invoices.detail.print")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500">{label}</dt>
      <dd className="font-medium text-neutral-900">{value}</dd>
    </div>
  );
}

function AmountRow({ label, value, className = "text-neutral-700" }: { label: string; value: string; className?: string }) {
  return (
    <div className={`flex justify-between gap-4 ${className}`}>
      <span>{label}</span>
      <span className="font-medium text-right break-all">{value}</span>
    </div>
  );
}
